import React, { JSX, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { computed } from "mobx"
import { useLocalObservable } from "mobx-react"
import { observer } from "mobx-react-lite"
import { Button, Col, Flex, Form, Input, Radio, Row, Select, Tag } from "antd"
import {
  CloseCircleFilled,
  CopyOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  PlusCircleOutlined,
  PlusOutlined,
  SaveOutlined,
} from "@ant-design/icons"
import Tippy from "@tippyjs/react"
import classNames from "classnames"

import { FormContainer } from "@src/app/components/FormContainer"
import { notify } from "@src/app/components/notify"
import { Uploader } from "@src/app/components/Uploader"
import { GlobalCurrenciesCollection } from "@src/app/CurrenciesCollection"
import { DefaultPageLayout } from "@src/app/layouts/DefaultPageLayout"
import { paths } from "@src/app/paths"
import UiStore from "@src/app/uiStore"
import { FormItemUi } from "@src/shared/componemts/FormItemUi"
import { ImageUploaderUI } from "@src/shared/componemts/ImageUploadUI"
import { ImageUpload } from "@src/shared/componemts/ImageUploadUI/entity/ImageUpload"
import { SelectCurrencyModal } from "@src/shared/componemts/SelectCurrencyModal/SelectCurrencyModal"
import { dateTimeFormat } from "@src/shared/date"
import { useCatchFormErrors } from "@src/shared/form/base_error"
import css from "./index.module.css"
import { ProjectUpsertStore } from "./store"

const { Item } = Form

export const ProjectUpsertPage = observer(() => {
  const navigate = useNavigate()
  const { isTransferioTheme } = UiStore()
  const { id } = useParams()
  const image = useLocalObservable(
    () => new ImageUpload({ uploadUrl: "/v2.0/projects/cache/image" }),
  )

  const [isOpen, setIsOpen] = useState(false)

  const logo = useLocalObservable(
    () => new ImageUpload({ uploadUrl: "/v2.0/projects/cache/logo" }),
  )

  const isNew = id === "new"

  const [isReadOnly, setIsReadOnly] = useState(!isNew)

  const [store] = useState(() => new ProjectUpsertStore(navigate))

  const [projectData, setProjectData] = useState<any | undefined>({})

  const currencies = GlobalCurrenciesCollection()
  const { isLoading, isUpserting, project, upsert } = store

  const [form] = Form.useForm()

  const title = isNew ? "Create a project" : `Project details`

  const themeLIst = computed(() => {
    return [
      ...(!isTransferioTheme ? [{ label: "Dark", value: "dark" }] : []),
      { label: "Light", value: "light" },
    ]
  }).get()

  const enabledCrypto = Form.useWatch("enabled_crypto", form) ?? []

  const onEnabledCryptoUpdate = (v: string[]) => {
    form.setFieldsValue({ enabled_crypto: v })
  }

  const buttonText = isNew ? "Create project" : "Save project"

  const { baseError, submit } = useCatchFormErrors(form, (values) => {
    const request = {
      ...values,
      image: { cache_id: image.cacheId, remove: image.isRemoved },
      logo: { cache_id: logo.cacheId, remove: logo.isRemoved },
    }

    return id === "new" ? upsert(request) : upsert(request, id)
  })

  useEffect(() => {
    if (id && id !== "new")
      project.load(id).then((res) => {
        const data = res?.data?.data

        form.setFieldsValue({
          callback_signature_key: data?.callback_signature_key,
          callback_url: data?.callback_url,
          created: data?.created_at ? dateTimeFormat(data?.created_at) : "",
          description: data?.description,
          enabled_crypto:
            data?.enabled_crypto?.map((c) => `${c.symbol}_${c.network}`) || [],
          id: data?.id,
          // @ts-ignore
          image: data?.image?.versions?.original,
          // @ts-ignore
          logo: data?.logo?.versions?.original,
          name: data?.name,
          return_url: data?.hosted?.return_url,
          status: data?.status,
          theme: data?.hosted?.theme,
        })

        setProjectData(data)
        logo.setState({ preview: (data?.logo?.versions as any)?.original })
        image.setState({
          preview: (data?.image?.versions as any)?.original,
        })
      })
  }, [id])

  const removeTag = (tag: string) => {
    form.setFieldsValue({
      enabled_crypto: enabledCrypto.filter((c: string) => c !== tag),
    })
  }

  const button = useMemo<JSX.Element>(() => {
    if (isReadOnly) {
      return (
        <Button
          htmlType="button"
          icon={<EditOutlined />}
          id="GENERATED_ID_PROJECTUPSERT_BUTTON_EDIT_1"
          loading={isUpserting}
          onClick={() => setIsReadOnly(false)}
          size="large"
          type="primary"
        >
          Edit
        </Button>
      )
    }

    return (
      <Button
        htmlType="submit"
        icon={isNew ? <PlusCircleOutlined /> : <SaveOutlined />}
        id="GENERATED_ID_PROJECTUPSERT_BUTTON_VOID_1"
        loading={isUpserting}
        onClick={() => submit(form.getFieldsValue())}
        size="large"
        type="primary"
      >
        {buttonText}
      </Button>
    )
  }, [isReadOnly])

  return (
    <DefaultPageLayout
      button={button}
      className={css.container}
      title={title}
      url={paths.projects}
    >
      <FormContainer
        baseError={baseError}
        className={classNames([css.formContainer])}
        disabled={isLoading || project.isLoading || isReadOnly}
        form={form}
        name="project"
      >
        <Row gutter={32}>
          <Col span={12}>
            <Flex gap={16} vertical>
              {!isNew && (
                <Item
                  extra="Create a Charge, Checkout, Invoice with the project ID"
                  label="Project ID"
                  name="id"
                >
                  <BlockedField value={form.getFieldValue("id")} />
                </Item>
              )}

              <Item
                label="Project name"
                name="name"
                rules={[{ message: "Please input name", required: true }]}
              >
                <Input />
              </Item>

              <Item
                label="Description"
                name="description"
                rules={[
                  { message: "Please input description", required: true },
                ]}
              >
                <Input />
              </Item>

              <Item
                initialValue={"active"}
                label="Status"
                name="status"
                required
              >
                <Radio.Group buttonStyle="solid">
                  <Radio.Button value="active">Active</Radio.Button>
                  <Radio.Button value="inactive">Inactive</Radio.Button>
                </Radio.Group>
              </Item>

              <Item
                label="Enabled crypto"
                name="enabled_crypto"
                rules={[
                  { message: "Please select at least one", required: true },
                ]}
              >
                <Row align="middle">
                  <Col span={6}>
                    <Button
                      onClick={() => setIsOpen(true)}
                      size={"small"}
                      type="primary"
                    >
                      <PlusCircleOutlined></PlusCircleOutlined>
                      Add crypto
                    </Button>
                  </Col>

                  <Col span={18}>
                    {enabledCrypto.length > 0 && (
                      <Flex
                        className={classNames({
                          [`${css.readOnly}`]: isReadOnly,
                        })}
                        gap={5}
                        wrap
                      >
                        {enabledCrypto.map((c: string) => {
                          const [symbol, network] = c.split("_")
                          const currency = currencies.crypto.find(
                            (i) => `${i.symbol}_${i.network}` === c,
                          )

                          return (
                            <Tag
                              closeIcon={<CloseCircleFilled />}
                              key={c}
                              onClose={() => removeTag(c)}
                            >
                              {currency?.symbol_alias ?? symbol}{" "}
                              {currency?.network ?? network}
                            </Tag>
                          )
                        })}
                      </Flex>
                    )}
                  </Col>
                </Row>
              </Item>
            </Flex>
          </Col>
          <Col span={12}>
            <Flex gap={16} vertical>
              {!isNew && (
                <Item
                  label="Callback Signature Key"
                  name="callback_signature_key"
                >
                  <BlockedField
                    isHidden
                    value={form.getFieldValue("callback_signature_key")}
                  />
                </Item>
              )}

              <FormItemUi label="Callback URL" name="callback_url" optional>
                <Input />
              </FormItemUi>

              <FormItemUi label={"Return URL"} name="return_url" optional>
                <Input />
              </FormItemUi>

              <Item
                initialValue="light"
                label="Payment page theme"
                name="theme"
              >
                <Select loading={isLoading} options={themeLIst} />
              </Item>

              <Item>
                <div className={css.uploaderWrapper}>
                  <ImageUploaderUI accept=".jpg,.jpeg" control={logo} />
                  <ImageUploaderUI accept=".jpg,.jpeg" control={image} />
                </div>
              </Item>
            </Flex>
          </Col>
        </Row>

        <SelectCurrencyModal
          currencies={currencies.crypto}
          initialCheckedList={enabledCrypto}
          onCancel={() => setIsOpen(false)}
          open={isOpen}
          update={onEnabledCryptoUpdate}
        ></SelectCurrencyModal>
      </FormContainer>
    </DefaultPageLayout>
  )
})
const BlockedField: React.FC<{
  disabled?: boolean
  isCopyable?: boolean
  isHidden?: boolean
  value: string
}> = ({ disabled, isCopyable = true, isHidden, value }) => {
  const [hidden, setHidden] = useState<boolean>(isHidden ? true : false)

  const onCopy = () => {
    navigator.clipboard.writeText(value).then(() => notify.success("Copied!"))
  }

  const EyeButton = hidden ? EyeInvisibleOutlined : EyeOutlined

  return (
    <div className={css.blockedContainer}>
      <Input
        disabled={disabled}
        value={hidden ? `${value?.slice(0, 8)}...${value?.slice(-8)}` : value}
      />
      <div className={css.blockedButtons}>
        {isHidden && <EyeButton onClick={() => setHidden(!hidden)} />}{" "}
        {isCopyable && (
          <Tippy className={css.tooltip} content="Copy to clipboard">
            <CopyOutlined onClick={onCopy} />
          </Tippy>
        )}
      </div>
    </div>
  )
}
